import { unstable_composeClasses as composeClasses } from '@mui/material';
import { capitalize } from '@mui/material/utils';
import clsx from 'clsx';
import {
  type DraggableProps as RBDDraggableProps,
  Draggable as RBDDraggable,
} from 'react-beautiful-dnd';

import { Flex } from '../../../flex';
import { DragIndicatorIcon } from '../../../icons';

import DraggableRoot, {
  type DraggableBaseProps,
  type DraggableOptionProps,
  DraggableColor,
  DraggableHandlerIcon,
  DraggableVariant,
  DraggableBehavior,
} from './DraggableBase';
import {
  type DraggableClasses,
  type DraggableClassKey,
  getDraggableUtilityClass,
} from './draggableClasses';

type DraggableProps = DraggableBaseProps & {
  children: React.ReactNode;
  nestedEl?: React.ReactNode | React.ReactNode[];
} & Pick<RBDDraggableProps, 'draggableId' | 'index'>;

function useUtilityClasses(ownerState: DraggableProps) {
  const { classes, color = DraggableColor.PRIMARY, variant = DraggableVariant.FLAT } = ownerState;

  const slots = {
    root: ['root', variant, `${variant}${capitalize(color)}`],
    flat: ['flat'],
    outlined: ['outlined'],
    dragIcon: ['dragIcon'],
  };

  return composeClasses(slots, getDraggableUtilityClass, classes);
}

const MAP_BEHAVIOR_TO_RBD_PROPS: {
  [key in DraggableBehavior]?: Partial<RBDDraggableProps>;
} = {
  [DraggableBehavior.DRAG_DISABLED]: { isDragDisabled: true },
};

function Draggable(props: DraggableProps) {
  const { root: classesRoot, dragIcon: dragIconClasses, ...classes } = useUtilityClasses(props);

  const {
    className,
    variant,
    color,
    disableGutters: isDisableGutters,
    children,
    nestedEl,
    draggableId,
    index,
    behavior = DraggableBehavior.DRAG_WITH_HANDLER,
    ...restProps
  } = props;

  const ownerState = {
    color,
    variant,
  };

  const rbdProps = MAP_BEHAVIOR_TO_RBD_PROPS[behavior] ?? {};

  if (
    behavior === DraggableBehavior.DRAG_WITHOUT_HANDLER ||
    behavior === DraggableBehavior.DRAG_DISABLED
  ) {
    return (
      <RBDDraggable draggableId={draggableId} index={index} {...rbdProps}>
        {(provided, snapshot) => (
          <DraggableRoot
            {...ownerState}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            ref={provided.innerRef}
            disableGutters={isDisableGutters}
            isDragging={snapshot.isDragging}
            className={clsx(classesRoot, className)}
            classes={classes}
            behavior={behavior}
            {...restProps}
          >
            <Flex sx={{ alignItems: 'center', width: 1 }}>{children}</Flex>
            {nestedEl}
          </DraggableRoot>
        )}
      </RBDDraggable>
    );
  }

  return (
    <RBDDraggable draggableId={draggableId} index={index} {...rbdProps}>
      {(provided, snapshot) => (
        <DraggableRoot
          {...ownerState}
          {...provided.draggableProps}
          ref={provided.innerRef}
          disableGutters={isDisableGutters}
          isDragging={snapshot.isDragging}
          className={clsx(classesRoot, className)}
          classes={classes}
          behavior={behavior}
          {...restProps}
        >
          <Flex sx={{ alignItems: 'center', width: 1 }}>
            <DraggableHandlerIcon
              {...provided.dragHandleProps}
              className={dragIconClasses}
              behavior={behavior}
            >
              <DragIndicatorIcon />
            </DraggableHandlerIcon>
            {children}
          </Flex>
          {nestedEl}
        </DraggableRoot>
      )}
    </RBDDraggable>
  );
}

export { DraggableBehavior };
export type {
  DraggableProps,
  DraggableBaseProps,
  DraggableOptionProps,
  DraggableClasses,
  DraggableClassKey,
};
export default Draggable;
